import { GeoCoordinate } from '@geofences/geofence';

export interface StaticMapPath {
  coordinates: GeoCoordinate[];
  /** Hex color like '#CE2525' */
  color: string;
}

export interface StaticMapOptions {
  paths: StaticMapPath[];
  key: string;
  /** Rendered size in CSS pixels, e.g. '400x200' */
  size?: string;
  scale?: 1 | 2;
}

const STATIC_MAP_BASE_URL = 'https://maps.googleapis.com/maps/api/staticmap';
/** Static Maps URLs are limited to 8192 characters — cap preview vertices well below that */
const MAX_PREVIEW_POINTS = 80;

/**
 * Encodes coordinates with Google's encoded polyline algorithm
 * https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */
export function encodePolyline(coordinates: GeoCoordinate[]): string {
  let previousLat = 0;
  let previousLng = 0;
  let result = '';

  for (const coordinate of coordinates) {
    const lat = Math.round(coordinate.latitude * 1e5);
    const lng = Math.round(coordinate.longitude * 1e5);
    result += encodeValue(lat - previousLat) + encodeValue(lng - previousLng);
    previousLat = lat;
    previousLng = lng;
  }

  return result;
}

function encodeValue(value: number): string {
  let encoded = value < 0 ? ~(value << 1) : value << 1;
  let result = '';

  while (encoded >= 0x20) {
    result += String.fromCodePoint((0x20 | (encoded & 0x1f)) + 63);
    encoded >>= 5;
  }
  result += String.fromCodePoint(encoded + 63);

  return result;
}

/** Thins a ring to at most maxPoints vertices — plenty of fidelity for a thumbnail */
export function decimateRing(coordinates: GeoCoordinate[], maxPoints = MAX_PREVIEW_POINTS): GeoCoordinate[] {
  if (coordinates.length <= maxPoints) {
    return coordinates;
  }

  const stride = Math.ceil(coordinates.length / maxPoints);
  return coordinates.filter((_, i) => i % stride === 0);
}

/** Appends the first coordinate when the ring isn't closed so filled paths render as shapes */
function closeRing(coordinates: GeoCoordinate[]): GeoCoordinate[] {
  const first = coordinates[0];
  const last = coordinates.at(-1)!;
  if (first.latitude === last.latitude && first.longitude === last.longitude) {
    return coordinates;
  }
  return [...coordinates, first];
}

function toStaticMapColor(hexColor: string, alpha: string = 'ff'): string {
  return `0x${hexColor.replace('#', '')}${alpha}`;
}

/**
 * Builds a Google Static Maps URL rendering the given polygons.
 * The map auto-fits to the paths since no center/zoom is supplied.
 * Returns null when there are no drawable paths.
 */
export function buildStaticMapUrl({ paths, key, size = '400x200', scale = 2 }: StaticMapOptions): string | null {
  const drawablePaths = paths.filter((p) => p.coordinates.length >= 3);
  if (!drawablePaths.length || !key) {
    return null;
  }

  const params = new URLSearchParams();
  params.append('size', size);
  params.append('scale', String(scale));
  params.append('maptype', 'roadmap');

  for (const path of drawablePaths) {
    const ring = closeRing(decimateRing(path.coordinates));
    const color = toStaticMapColor(path.color);
    const fillColor = toStaticMapColor(path.color, '40');
    params.append('path', `color:${color}|fillcolor:${fillColor}|weight:2|enc:${encodePolyline(ring)}`);
  }

  params.append('key', key);

  return `${STATIC_MAP_BASE_URL}?${params.toString()}`;
}
